/**
 * Centred status pill inside the transcript — "Call connected", "Agent left".
 *
 * The dot colours are literal rather than theme tokens because they carry
 * meaning (green = fine, red = failed) that has to survive any brand palette;
 * web hardcodes the same two values.
 */

import type { FC } from "react";
import { Platform, StyleSheet, Text, View } from "react-native";

import { solidColor } from "../lib/gradient";
import { ringgId } from "../lib/testing";
import { resolveFontFamily, useWidgetTheme } from "../lib/widget-theme";

type SystemLogLevel = "info" | "error";

type SystemLogProps = {
  label: string;
  meta?: string;
  level?: SystemLogLevel;
};

const INFO_DOT_COLOR = "#22c55e";
const ERROR_DOT_COLOR = "#ef4444";

/** The `font-mono` of the meta span; RN needs a real family per platform. */
const MONOSPACE_FAMILY = Platform.select({ ios: "Menlo", android: "monospace" });

const dotColorFor = (level: SystemLogLevel): string => {
  switch (level) {
    case "error":
      return ERROR_DOT_COLOR;
    case "info":
    default:
      return INFO_DOT_COLOR;
  }
};

const SystemLog: FC<SystemLogProps> = ({ label, meta, level = "info" }) => {
  const theme = useWidgetTheme();
  const fontFamily = resolveFontFamily(theme.fontFamily);

  // Web also tags the row with `data-ringg-level`; RN nodes carry a single
  // testID, so the level stays observable through the dot colour alone.
  return (
    <View testID={ringgId("system-log")} style={styles.row}>
      {/* Web's `role="status"` is a polite live region; RN exposes that on Android
          only, and the `text` role keeps the pill one announcement on both. */}
      <View
        testID={ringgId("system-log-pill")}
        accessibilityRole="text"
        accessibilityLiveRegion="polite"
        style={[styles.pill, { borderColor: solidColor(theme.borderColor), backgroundColor: theme.backgroundColor }]}
      >
        <View testID={ringgId("system-log-dot")} style={[styles.dot, { backgroundColor: dotColorFor(level) }]} />
        <Text testID={ringgId("system-log-label")} numberOfLines={1} style={[styles.label, { color: theme.textColor, fontFamily }]}>
          {label}
        </Text>
        {meta ? (
          // Web puts the full string in a `title` tooltip for the truncated case.
          <Text testID={ringgId("system-log-meta")} numberOfLines={1} accessibilityLabel={meta} style={[styles.meta, { color: theme.mutedTextColor }]}>
            {meta}
          </Text>
        ) : null}
      </View>
    </View>
  );
};

export default SystemLog;

const styles = StyleSheet.create({
  row: { flexDirection: "row", justifyContent: "center", marginVertical: 4 },
  pill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 9999,
    borderWidth: 1,
    // `max-w-full`: the pill may shrink, but never past the transcript width.
    flexShrink: 1,
  },
  dot: { width: 6, height: 6, borderRadius: 3, flexShrink: 0 },
  label: { fontSize: 11, fontWeight: "500", flexShrink: 1 },
  meta: { fontSize: 11, fontFamily: MONOSPACE_FAMILY, flexShrink: 1 },
});
